import { useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import { ADD_FILES } from '@gql/file';

const ATTACH_FILES_TO_POST = gql`
  mutation AttachFilesToPost($_id: ID!, $fileIds: [ID!]!) {
    attachFilesToPost(_id: $_id, fileIds: $fileIds) {
      _id
      title
      html
      isPublished
      isPinned
      deletedAt
    }
  }
`;


const useAttachFilesToPost = () => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [progress, setProgress] = useState<number>(0);

    const [uploadFiles] = useMutation(ADD_FILES);
    const [attachFiles] = useMutation(ATTACH_FILES_TO_POST);

    /**
     * Attach files to a post, with upload progress support.
     * @param postId The post ID
     * @param files FileList or File[]
     * @param onProgress Optional progress callback
     */
    const attachFilesToPost = async (
        postId: string,
        files: FileList | File[],
        onProgress?: (ev: ProgressEvent) => void
    ) => {
        setUploading(true);
        setError(null);
        setProgress(0);

        try {
            // Convert FileList to Array and filter only File instances
            const fileArray = Array.from(files).filter(f => f instanceof File);

            if (!fileArray.length) throw new Error('No valid files to upload');


            // 1. Upload files to get their IDs
            const { data: uploadData } = await uploadFiles({
                variables: { files: fileArray, context: 'blog_post_file' },
                context: {
                    fetchOptions: {
                        useUpload: true,
                        onProgress: (ev: ProgressEvent) => {
                            const percent = Math.round((ev.loaded / ev.total) * 100);
                            console.log(`Upload progress: ${percent}%`);
                            setProgress(percent);
                            if (onProgress) onProgress(ev);
                        },
                    },
                },
            });

            const fileIds = uploadData?.addFiles?.map((file: any) => file._id);
            if (!fileIds || !fileIds.length) throw new Error('File upload failed');

            // 2. Attach files to the post
            await attachFiles({
                variables: { _id: postId, fileIds },
            });

            setProgress(100);
            return uploadData?.addFiles;
        } catch (err: any) {
            setError(err.message);
            return null;
        } finally {
            setUploading(false);
        }
    };

    return { attachFilesToPost, uploading, error, progress };
};

export default useAttachFilesToPost;
